import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { ClaimType, EpistemicStatus, Necessity } from '@et/schema';
import { StatusMark } from '../lexicon/StatusMark.js';
import { Tag, FallbackBadge } from '../lexicon/tags.js';

export interface ClaimSummary {
  readonly id: string;
  readonly title: string;
  readonly religionId: string;
  readonly claimType: ClaimType;
  readonly foundational: boolean;
  readonly necessity: Necessity | null;
  readonly historicalStatus: EpistemicStatus;
  readonly counterpartClaimId?: string | null;
  readonly fallbackFields?: readonly string[];
}

export interface BoldClaimCardProps {
  readonly claim: ClaimSummary;
  readonly href?: string;
  readonly counterpartTitle?: string | null;
  /** Onde abre o par. Sem isto o titulo do par e so texto — e era. */
  readonly counterpartHref?: string;
  readonly onOpen?: () => void;
  readonly className?: string;
}

/**
 * §4 — um bold claim nao e uma camada nova da hierarquia: e um claim marcado
 * `foundational`, e a ★ diz exactamente isso.
 *
 * O aviso de que necessario nao e suficiente fica no card e nao numa nota de
 * rodape, porque e o erro que este ecra convida a cometer: ver "estabelecido"
 * ao lado de "necessario para a tradicao" e ler as duas coisas como uma so.
 */
export function BoldClaimCard({
  claim, href, counterpartTitle, counterpartHref, onOpen, className,
}: BoldClaimCardProps) {
  const { t } = useTranslation();
  const Title = href === undefined ? 'span' : 'a';

  return (
    <article
      className={clsx('et-card', 'et-boldclaim', className)}
      data-claim={claim.id}
      data-foundational={claim.foundational ? 'true' : 'false'}
      data-testid="bold-claim"
    >
      <div className="et-boldclaim__head">
        {claim.foundational && (
          <span className="et-boldclaim__star" title={t('claim.foundational')}>
            <span aria-hidden="true">★</span>
            <span className="et-visually-hidden">{t('claim.foundational')}</span>
          </span>
        )}
        <StatusMark status={claim.historicalStatus} size={20} decorative />
        <h3 className="et-boldclaim__title">
          <Title
            {...(href === undefined ? {} : { href })}
            {...(onOpen === undefined ? {} : { onClick: onOpen })}
          >
            {claim.title}
          </Title>
          {claim.fallbackFields?.includes('title') === true && <FallbackBadge />}
        </h3>
      </div>

      <div className="et-boldclaim__tags">
        <Tag tone="claim">{t(`lexicon:claimType.${claim.claimType}`)}</Tag>
        <Tag>{t(`lexicon:status.${claim.historicalStatus}`)}</Tag>
        {claim.necessity !== null && <Tag>{t(`lexicon:necessity.${claim.necessity}`)}</Tag>}
      </div>

      {claim.foundational && claim.necessity === 'necessary' && (
        <p className="et-boldclaim__caveat" data-testid="necessity-caveat">
          {t('claim.necessaryNotSufficient')}
        </p>
      )}

      {claim.counterpartClaimId != null && (
        <p className="et-boldclaim__counterpart">
          <span className="et-boldclaim__counterpart-label">{t('claim.counterpart')}</span>
          {counterpartHref === undefined
            ? (counterpartTitle ?? claim.counterpartClaimId)
            : <a href={counterpartHref}>{counterpartTitle ?? claim.counterpartClaimId}</a>}
        </p>
      )}
    </article>
  );
}
